import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Server,
  Mail,
  Database,
  Cloud,
  Zap,
  Shield,
  ShieldCheck,
  Settings,
  ArrowRight,
  ArrowUp,
  Menu,
  X,
  Sparkles,
  Copy,
  Check,
  Rocket,
  Layers,
  LifeBuoy,
  HelpCircle,
  ChevronDown,
  GitBranch,
  Cpu,
  Terminal,
  Boxes,
  Code2,
  Search,
  PenTool,
  Activity,
  Clock,
} from "lucide-react";
import * as THREE from "three";

/* --------------------------------------------------------------------------
 * Design tokens
 * ------------------------------------------------------------------------ */
const COLORS = {
  cyan: "#06b6d4",
  purple: "#a855f7",
  pink: "#ec4899",
  amber: "#f59e0b",
  bg: "#000000",
  textMuted: "#9ca3af",
  textSoft: "#d1d5db",
};

const GRADIENT_BRAND = `linear-gradient(90deg, ${COLORS.cyan}, ${COLORS.purple}, ${COLORS.pink})`;

/* --------------------------------------------------------------------------
 * One-time injected keyframes / utility classes.
 * Kept as a single <style> tag so the rest of the component can stay with
 * the same inline-style approach already used across this file, while still
 * getting real CSS animations, hover states, and prefers-reduced-motion
 * support that inline styles alone can't express.
 * ------------------------------------------------------------------------ */
const GlobalAnimationStyles = () => (
  <style>{`
    @keyframes syl-fade-up {
      from { opacity: 0; transform: translateY(28px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes syl-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes syl-float {
      0%, 100% { transform: translateY(0px); }
      50%      { transform: translateY(-10px); }
    }
    @keyframes syl-pulse-ring {
      0%   { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0.45); }
      70%  { box-shadow: 0 0 0 14px rgba(6, 182, 212, 0); }
      100% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0); }
    }
    @keyframes syl-gradient-pan {
      0%   { background-position: 0% 50%; }
      50%  { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes syl-shimmer {
      0%   { transform: translateX(-120%); }
      100% { transform: translateX(220%); }
    }
    @keyframes syl-spin-slow {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }

    .syl-reveal {
      opacity: 0;
      transform: translateY(28px);
      transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
                  transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      will-change: opacity, transform;
    }
    .syl-reveal.syl-in {
      opacity: 1;
      transform: translateY(0);
    }

    .syl-brand-gradient-text {
      background-size: 200% 200%;
      animation: syl-gradient-pan 6s ease infinite;
    }

    .syl-btn-primary {
      position: relative;
      overflow: hidden;
      transform: translateY(0) scale(1);
    }
    .syl-btn-primary:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 14px 30px rgba(6, 182, 212, 0.35);
    }
    .syl-btn-primary:active {
      transform: translateY(-1px) scale(0.99);
    }
    .syl-btn-primary::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 40%;
      height: 100%;
      background: linear-gradient(
        120deg,
        transparent,
        rgba(255, 255, 255, 0.35),
        transparent
      );
      transform: translateX(-120%);
    }
    .syl-btn-primary:hover::after {
      animation: syl-shimmer 0.9s ease forwards;
    }

    .syl-btn-outline {
      transition: background 0.3s ease, transform 0.3s ease,
        box-shadow 0.3s ease, border-color 0.3s ease;
    }
    .syl-btn-outline:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 22px rgba(6, 182, 212, 0.18);
    }

    .syl-nav-link {
      position: relative;
    }
    .syl-nav-link::after {
      content: "";
      position: absolute;
      left: 0.75rem;
      right: 0.75rem;
      bottom: 0.2rem;
      height: 2px;
      background: ${GRADIENT_BRAND};
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
      border-radius: 2px;
    }
    .syl-nav-link:hover::after,
    .syl-nav-link.syl-active::after {
      transform: scaleX(1);
    }

    .syl-product-card {
      position: relative;
      transform-style: preserve-3d;
      transition: transform 0.2s ease, border-color 0.3s ease,
        box-shadow 0.3s ease;
    }
    .syl-product-card:hover {
      border-color: rgba(255, 255, 255, 0.3) !important;
      box-shadow: 0 20px 45px rgba(0, 0, 0, 0.45);
    }
    .syl-product-card::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(
        135deg,
        rgba(6, 182, 212, 0.6),
        rgba(168, 85, 247, 0.6),
        rgba(236, 72, 153, 0.6)
      );
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }
    .syl-product-card:hover::before {
      opacity: 1;
    }

    .syl-icon-float {
      animation: syl-float 4s ease-in-out infinite;
    }

    .syl-chip {
      transition: transform 0.25s ease, background 0.25s ease,
        border-color 0.25s ease;
    }
    .syl-chip:hover {
      transform: translateY(-3px);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .syl-checklist-item {
      opacity: 0;
      transform: translateX(-12px);
      animation: syl-fade-up 0.6s ease forwards;
    }

    .syl-drawer-backdrop {
      transition: opacity 0.3s ease;
    }

    .syl-scrollbar-hide::-webkit-scrollbar {
      display: none;
    }

    .syl-fab {
      transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1),
        opacity 0.3s ease, box-shadow 0.3s ease;
    }
    .syl-fab:hover {
      transform: translateY(-4px) scale(1.08);
      box-shadow: 0 12px 28px rgba(168, 85, 247, 0.45);
    }

    .syl-badge-pulse {
      animation: syl-pulse-ring 2.4s ease-in-out infinite;
    }

    .syl-copy-btn {
      transition: background 0.2s ease, transform 0.15s ease;
    }
    .syl-copy-btn:active {
      transform: scale(0.94);
    }

    .syl-value-card {
      position: relative;
      transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
        border-color 0.35s ease, box-shadow 0.35s ease;
    }
    .syl-value-card:hover {
      transform: translateY(-8px);
      border-color: rgba(255, 255, 255, 0.25) !important;
      box-shadow: 0 22px 40px rgba(0, 0, 0, 0.4);
    }
    .syl-value-card:hover .syl-value-icon-wrap {
      transform: scale(1.1) rotate(-4deg);
    }
    .syl-value-icon-wrap {
      transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .syl-tech-badge {
      transition: transform 0.25s ease, border-color 0.25s ease,
        background 0.25s ease, box-shadow 0.25s ease;
    }
    .syl-tech-badge:hover {
      transform: translateY(-4px) scale(1.05);
      border-color: rgba(6, 182, 212, 0.5);
      background: rgba(6, 182, 212, 0.08) !important;
      box-shadow: 0 10px 24px rgba(6, 182, 212, 0.2);
    }

    .syl-process-step {
      position: relative;
    }
    .syl-process-number {
      transition: transform 0.35s ease, box-shadow 0.35s ease;
    }
    .syl-process-step:hover .syl-process-number {
      transform: scale(1.12);
      box-shadow: 0 0 0 6px rgba(6, 182, 212, 0.15);
    }

    .syl-faq-item {
      transition: border-color 0.3s ease, background 0.3s ease;
    }
    .syl-faq-item:hover {
      border-color: rgba(255, 255, 255, 0.22) !important;
    }
    .syl-faq-question {
      transition: color 0.25s ease;
    }
    .syl-faq-chevron {
      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
      flex-shrink: 0;
    }
    .syl-faq-chevron.syl-faq-open {
      transform: rotate(180deg);
    }
    .syl-faq-answer {
      display: grid;
      grid-template-rows: 0fr;
      opacity: 0;
      transition: grid-template-rows 0.35s cubic-bezier(0.16, 1, 0.3, 1),
        opacity 0.3s ease;
    }
    .syl-faq-answer.syl-faq-open {
      grid-template-rows: 1fr;
      opacity: 1;
    }
    .syl-faq-answer-inner {
      overflow: hidden;
      min-height: 0;
    }

    .syl-stat-number {
      background-image: ${GRADIENT_BRAND};
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    @media (prefers-reduced-motion: reduce) {
      .syl-reveal {
        transition: none !important;
        opacity: 1 !important;
        transform: none !important;
      }
      .syl-brand-gradient-text,
      .syl-icon-float,
      .syl-badge-pulse,
      .syl-checklist-item {
        animation: none !important;
      }
      .syl-product-card,
      .syl-btn-primary,
      .syl-btn-outline,
      .syl-fab,
      .syl-value-card,
      .syl-value-icon-wrap,
      .syl-tech-badge,
      .syl-process-number {
        transition: none !important;
      }
    }

    :focus-visible {
      outline: 2px solid ${COLORS.cyan};
      outline-offset: 3px;
      border-radius: 4px;
    }
  `}</style>
);

/* --------------------------------------------------------------------------
 * Reveal-on-scroll wrapper. Adds the "syl-in" class once the element enters
 * the viewport so content animates in gracefully instead of popping in all
 * at once - a small touch that makes long pages feel considered rather than
 * dumped on the visitor.
 * ------------------------------------------------------------------------ */
const Reveal = ({ children, delay = 0, style = {}, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`syl-reveal${visible ? " syl-in" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
};

/* --------------------------------------------------------------------------
 * Product card with a subtle pointer-driven 3D tilt. Falls back to a plain
 * hover scale on touch devices / reduced motion.
 * ------------------------------------------------------------------------ */
const ProductCard = ({ product }) => {
  const cardRef = useRef(null);
  const isComingSoon = Boolean(product.comingSoon);

  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const rotateX = ((y - rect.height / 2) / rect.height) * -8;
    const rotateY = ((x - rect.width / 2) / rect.width) * 8;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.03)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform =
      "perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)";
  };

  const openProduct = () => {
    if (isComingSoon) return;
    if (product.url.includes("@")) {
      window.location.href = `mailto:${product.url}`;
    } else if (product.url.startsWith("http")) {
      window.open(product.url, "_blank", "noopener,noreferrer");
    } else {
      window.open(`https://${product.url}`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      ref={cardRef}
      className="syl-product-card"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: "relative",
        background: product.gradient,
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "1rem",
        padding: "2rem",
        textAlign: "center",
        cursor: isComingSoon ? "default" : "pointer",
      }}
      role={isComingSoon ? undefined : "button"}
      tabIndex={isComingSoon ? undefined : 0}
      onClick={isComingSoon ? undefined : openProduct}
      onKeyDown={
        isComingSoon
          ? undefined
          : (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openProduct();
              }
            }
      }
    >
      {isComingSoon && (
        <div
          style={{
            position: "absolute",
            top: "-14px",
            right: "1.5rem",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
            background: `linear-gradient(90deg, ${COLORS.amber}, #f97316)`,
            color: "#1a1200",
            fontSize: "0.7rem",
            fontWeight: 700,
            padding: "0.35rem 0.85rem",
            borderRadius: "999px",
            letterSpacing: "0.03em",
          }}
        >
          <Clock size={12} />
          COMING SOON
        </div>
      )}

      <div
        className="syl-icon-float"
        style={{ marginBottom: "1.5rem", opacity: isComingSoon ? 0.85 : 1 }}
      >
        {product.icon}
      </div>
      <h3
        style={{
          fontSize: "1.5rem",
          fontWeight: "bold",
          color: "white",
          marginBottom: "1rem",
        }}
      >
        {product.name}
      </h3>
      <p
        style={{
          color: COLORS.textSoft,
          lineHeight: "1.6",
          marginBottom: "1.5rem",
        }}
      >
        {product.description}
      </p>
      {isComingSoon ? (
        <div
          style={{
            background: "rgba(245, 158, 11, 0.12)",
            border: "1px solid rgba(245, 158, 11, 0.35)",
            color: COLORS.amber,
            padding: "0.5rem 1rem",
            borderRadius: "2rem",
            fontSize: "0.9rem",
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          <Clock size={14} />
          Launching soon
        </div>
      ) : (
        <div
          style={{
            background: "rgba(255, 255, 255, 0.1)",
            color: COLORS.cyan,
            padding: "0.5rem 1rem",
            borderRadius: "2rem",
            fontSize: "0.9rem",
            fontFamily: "monospace",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.4rem",
          }}
        >
          {product.url}
          <ArrowRight size={14} />
        </div>
      )}
    </div>
  );
};

const Sylwave = () => {
  const mountRef = useRef(null);
  const [currentSection, setCurrentSection] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  const sectionIds = [
    "home",
    "products",
    "why-us",
    "tech",
    "process",
    "hosting",
    "faq",
    "contact",
  ];

  const sectionLabels = {
    home: "Home",
    products: "Products",
    "why-us": "Why Us",
    tech: "Tech Stack",
    process: "Process",
    hosting: "Hosting",
    faq: "FAQ",
    contact: "Contact",
  };

  // Lock body scroll when drawer open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Detect mobile viewport
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const query = "(max-width: 767px)";
    const mql = window.matchMedia(query);
    const handleChange = (e) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", handleChange);
    } else if (mql.addListener) {
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", handleChange);
      } else if (mql.removeListener) {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  // Metadata based on domain
  useEffect(() => {
    const host = window?.location?.hostname || "";
    const isTs = host.includes("ts.sylwave.com");
    const title = isTs ? "Sylwave — Text Story Generator" : "Sylwave";
    const description = isTs
      ? "Generate engaging stories from text with AI."
      : "Sylwave — Creating cool technologies.";
    const url = isTs ? "https://ts.sylwave.com" : "https://sylwave.com";
    const image = `${window.location.origin}/buddy.ico`;

    document.title = title;

    const ensureMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}='${key}']`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    ensureMeta("name", "description", description);
    ensureMeta("property", "og:type", "website");
    ensureMeta("property", "og:url", url);
    ensureMeta("property", "og:title", title);
    ensureMeta("property", "og:description", description);
    ensureMeta("property", "og:image", image);

    ensureMeta("property", "twitter:card", "summary_large_image");
    ensureMeta("property", "twitter:url", url);
    ensureMeta("property", "twitter:title", title);
    ensureMeta("property", "twitter:description", description);
    ensureMeta("property", "twitter:image", image);
  }, []);

  // Scroll progress bar + back-to-top visibility
  useEffect(() => {
    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      setScrollProgress(height > 0 ? (scrollTop / height) * 100 : 0);
      setShowBackToTop(scrollTop > 480);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Scrollspy: keep nav highlight in sync even when the user free-scrolls
  // instead of clicking a nav link.
  useEffect(() => {
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (sections.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          setCurrentSection(visible.target.id);
        }
      },
      { threshold: [0.3, 0.5, 0.7], rootMargin: "-90px 0px -40% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
    // Intentionally empty deps: sectionIds is a fixed, static list of ids
    // and re-running this on every render would just re-attach the same
    // observers.
  }, []);

  // Three.js 3D Background Setup, with a soft mouse-parallax and a graceful
  // fade-in so the scene doesn't just pop onto a black screen.
  useEffect(() => {
    if (!mountRef.current) return undefined;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.opacity = "0";
    renderer.domElement.style.transition = "opacity 1.2s ease";
    mountRef.current.appendChild(renderer.domElement);

    requestAnimationFrame(() => {
      renderer.domElement.style.opacity = "1";
      setHeroLoaded(true);
    });

    const geometries = [
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.SphereGeometry(0.7, 16, 16),
      new THREE.ConeGeometry(0.7, 1.5, 8),
      new THREE.OctahedronGeometry(0.8),
      new THREE.TorusGeometry(0.6, 0.2, 10, 24),
      new THREE.IcosahedronGeometry(0.75),
    ];

    const materials = [
      new THREE.MeshBasicMaterial({
        color: 0xff00ff,
        wireframe: true,
        transparent: true,
        opacity: 0.6,
      }),
      new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        wireframe: true,
        transparent: true,
        opacity: 0.6,
      }),
      new THREE.MeshBasicMaterial({
        color: 0xffff00,
        wireframe: true,
        transparent: true,
        opacity: 0.6,
      }),
      new THREE.MeshBasicMaterial({
        color: 0xa855f7,
        wireframe: true,
        transparent: true,
        opacity: 0.55,
      }),
    ];

    const meshes = [];
    for (let i = 0; i < 20; i++) {
      const geometry =
        geometries[Math.floor(Math.random() * geometries.length)];
      const material = materials[Math.floor(Math.random() * materials.length)];
      const mesh = new THREE.Mesh(geometry, material);

      mesh.position.set(
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22,
        (Math.random() - 0.5) * 22
      );

      mesh.rotation.set(
        Math.random() * Math.PI,
        Math.random() * Math.PI,
        Math.random() * Math.PI
      );

      scene.add(mesh);
      meshes.push(mesh);
    }

    // A soft field of tiny particles behind the wireframe shapes - cheap to
    // render (a single draw call) but adds a lot of depth/parallax, giving
    // the whole hero a "starfield" / next-gen feel rather than a flat void.
    const starCount = 500;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      starPositions[i * 3] = (Math.random() - 0.5) * 60;
      starPositions[i * 3 + 1] = (Math.random() - 0.5) * 60;
      starPositions[i * 3 + 2] = (Math.random() - 0.5) * 60;
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute(
      "position",
      new THREE.BufferAttribute(starPositions, 3)
    );
    const starMaterial = new THREE.PointsMaterial({
      color: 0x67e8f9,
      size: 0.05,
      transparent: true,
      opacity: 0.55,
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);

    camera.position.z = 10;

    // Gentle parallax: the whole field drifts slightly toward the cursor.
    const pointer = { x: 0, y: 0 };
    const handlePointerMove = (e) => {
      pointer.x = (e.clientX / window.innerWidth - 0.5) * 2;
      pointer.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    if (!prefersReducedMotion) {
      window.addEventListener("pointermove", handlePointerMove);
    }

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);

      if (!prefersReducedMotion) {
        meshes.forEach((mesh, index) => {
          mesh.rotation.x += 0.01 + index * 0.001;
          mesh.rotation.y += 0.01 + index * 0.001;
          mesh.position.y += Math.sin(Date.now() * 0.001 + index) * 0.01;
        });
        stars.rotation.y += 0.0006;
        stars.rotation.x += 0.0002;
        camera.position.x += (pointer.x * 1.2 - camera.position.x) * 0.03;
        camera.position.y += (-pointer.y * 1.2 - camera.position.y) * 0.03;
        camera.lookAt(0, 0, 0);
      }

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", handlePointerMove);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      starGeometry.dispose();
      starMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setCurrentSection(sectionId);
    }
    setMobileMenuOpen(false);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyEmail = async (e) => {
    e.stopPropagation();
    const email = "support@sylwave.com";
    try {
      await navigator.clipboard.writeText(email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (older browser / permissions) - the
      // mailto link right next to this button still works fine.
    }
  };

  const products = [
    {
      name: "System Design Platform",
      url: "leetdesigner.com",
      description:
        "Advanced system design tools and architecture patterns for modern applications",
      icon: <Settings size={48} style={{ color: COLORS.cyan }} />,
      gradient:
        "linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(168, 85, 247, 0.2))",
    },
    // Commented out for now - Text Story Generator
    // {
    //   name: "Text Story Generator",
    //   url: "ts.sylwave.com",
    //   description: "This is a story generator from text",
    //   icon: <Globe size={48} style={{ color: "#22d3ee" }} />,
    //   gradient:
    //     "linear-gradient(135deg, rgba(34, 211, 238, 0.2), rgba(168, 85, 247, 0.2))",
    // },
    // Commented out for now - LinkedIn Buddy
    // {
    //   name: "LinkedIn Buddy",
    //   url: "linkedinbuddy.in",
    //   description:
    //     "Revolutionary job platform connecting talent with opportunities",
    //   icon: <Users size={48} style={{ color: "#a855f7" }} />,
    //   gradient:
    //     "linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(236, 72, 153, 0.2))",
    // },
    {
      name: "Cloud Infrastructure",
      url: "support@sylwave.com",
      description:
        "Enterprise-grade hosting solutions with Kubernetes, HA, caching, and more",
      icon: <Cloud size={48} style={{ color: COLORS.pink }} />,
      gradient:
        "linear-gradient(135deg, rgba(236, 72, 153, 0.2), rgba(6, 182, 212, 0.2))",
      comingSoon: true,
    },
  ];

  const capabilityChips = [
    { icon: <Database size={18} />, label: "High Availability" },
    { icon: <Zap size={18} />, label: "Advanced Caching" },
    { icon: <Shield size={18} />, label: "Kubernetes Ready" },
    { icon: <Cloud size={18} />, label: "24/7 Monitoring" },
  ];

  const whyUsItems = [
    {
      icon: <Rocket size={30} style={{ color: COLORS.cyan }} />,
      title: "Built For Speed",
      description:
        "Every product is engineered for low latency from the ground up - optimized bundles, edge-friendly assets, and caching that actually gets used.",
    },
    {
      icon: <ShieldCheck size={30} style={{ color: COLORS.purple }} />,
      title: "Security First",
      description:
        "Encrypted connections, hardened containers, and least-privilege access controls are the default on everything we ship, not an afterthought.",
    },
    {
      icon: <Layers size={30} style={{ color: COLORS.pink }} />,
      title: "Scales With You",
      description:
        "A Kubernetes-native foundation means your infrastructure grows from your first ten users to your millionth without a rewrite.",
    },
    {
      icon: <LifeBuoy size={30} style={{ color: COLORS.cyan }} />,
      title: "Support From Real Engineers",
      description:
        "When you email support@sylwave.com, a real engineer who understands your stack replies - no ticket queues, no scripted chatbots.",
    },
  ];

  const techStack = [
    { icon: <Code2 size={22} />, label: "React & TypeScript" },
    { icon: <Server size={22} />, label: "Node.js" },
    { icon: <Boxes size={22} />, label: "Kubernetes" },
    { icon: <Terminal size={22} />, label: "Docker" },
    { icon: <Database size={22} />, label: "PostgreSQL" },
    { icon: <Zap size={22} />, label: "Redis" },
    { icon: <Cloud size={22} />, label: "Cloud-Native Hosting" },
    { icon: <GitBranch size={22} />, label: "CI/CD Pipelines" },
    { icon: <Cpu size={22} />, label: "Three.js / WebGL" },
    { icon: <Activity size={22} />, label: "24/7 Monitoring" },
  ];

  const processSteps = [
    {
      icon: <Search size={26} style={{ color: COLORS.cyan }} />,
      title: "Discovery & Scoping",
      description:
        "We start by understanding your goals, users, and constraints - so what we build actually solves the right problem.",
    },
    {
      icon: <PenTool size={26} style={{ color: COLORS.purple }} />,
      title: "Architecture & Design",
      description:
        "We design a system that fits today's scale and tomorrow's - covering data models, infrastructure, and the UI itself.",
    },
    {
      icon: <Terminal size={26} style={{ color: COLORS.pink }} />,
      title: "Build & Deploy",
      description:
        "Clean, tested code shipped through automated pipelines onto Kubernetes-backed infrastructure with zero-downtime releases.",
    },
    {
      icon: <Activity size={26} style={{ color: COLORS.cyan }} />,
      title: "Monitor & Support",
      description:
        "Once you're live, 24/7 monitoring and a direct line to our engineers keep things running smoothly long after launch day.",
    },
  ];

  const faqItems = [
    {
      question: "Is the enterprise hosting package available right now?",
      answer:
        "It's currently in final testing and not yet open for sign-ups. Once it launches, it will bundle Kubernetes orchestration, managed database setup, load balancing with high availability, Redis caching, message queues, and 24/7 monitoring. Email us to get notified the moment it's live.",
    },
    {
      question: "Can the infrastructure be customized to my project?",
      answer:
        "That's the plan - the standard package covers a solid enterprise default, but the underlying architecture is modular so compute, caching, and monitoring can be tailored to your traffic patterns and budget once it launches.",
    },
    {
      question: "Is the System Design Platform live?",
      answer:
        "Yes - leetdesigner.com is live today. It's the one product you can go use right now; everything else on this page is either shipping or in active development.",
    },
    {
      question: "Do you provide support after launch?",
      answer:
        "Always. Every engagement includes 24/7 monitoring and direct email support at support@sylwave.com, so issues get caught - and answered by an actual engineer - long before they become outages.",
    },
    {
      question: "What technologies do you build with?",
      answer:
        "Our stack centers on React and TypeScript on the frontend, Node.js services, Kubernetes and Docker for orchestration, PostgreSQL and Redis for data, and automated CI/CD pipelines for deployment.",
    },
    {
      question: "How do I get started?",
      answer:
        "Reach out at support@sylwave.com with a short description of what you're building. We'll follow up to schedule a discovery call and map out next steps from there.",
    },
  ];

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        backgroundColor: COLORS.bg,
        color: "white",
        overflowX: "hidden",
      }}
    >
      <GlobalAnimationStyles />

      {/* Scroll progress bar */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          height: "3px",
          width: `${scrollProgress}%`,
          background: GRADIENT_BRAND,
          zIndex: 300,
          transition: "width 0.1s linear",
        }}
      />

      {/* 3D Background - Lowest layer */}
      <div
        ref={mountRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      />

      {/* Gradient Overlays */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(135deg, rgba(88, 28, 135, 0.3) 0%, transparent 50%, rgba(22, 78, 99, 0.3) 100%)",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 50%, rgba(0, 0, 0, 0.4) 100%)",
          pointerEvents: "none",
          zIndex: 3,
        }}
      />

      {/* Navigation */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          padding: "1rem 0",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 2rem",
          }}
        >
          <button
            onClick={() => scrollToSection("home")}
            aria-label="SylWave home"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <h1
              className="syl-brand-gradient-text"
              style={{
                fontSize: "2rem",
                fontWeight: "bold",
                backgroundImage: GRADIENT_BRAND,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                margin: 0,
              }}
            >
              SylWave
            </h1>
          </button>

          {/* Desktop Menu */}
          {!isMobile && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.15rem",
                justifyContent: "flex-end",
              }}
            >
              {sectionIds.map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  aria-current={currentSection === section ? "page" : undefined}
                  className={`syl-nav-link${
                    currentSection === section ? " syl-active" : ""
                  }`}
                  style={{
                    background: "none",
                    border: "none",
                    color: currentSection === section ? COLORS.cyan : "white",
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "color 0.3s ease",
                    padding: "0.5rem 0.6rem",
                  }}
                >
                  {sectionLabels[section] || section}
                </button>
              ))}
            </div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                background: "none",
                border: "none",
                color: "white",
                cursor: "pointer",
                padding: "0.5rem",
              }}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>

        {/* Mobile Drawer Menu - always mounted so the slide transition can
            actually play, visibility toggled via transform/opacity. */}
        {isMobile && (
          <div
            className="syl-drawer-backdrop"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 200,
              background: "rgba(0,0,0,0.6)",
              opacity: mobileMenuOpen ? 1 : 0,
              pointerEvents: mobileMenuOpen ? "auto" : "none",
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                height: "100%",
                width: "80%",
                maxWidth: "320px",
                background: "rgba(0, 0, 0, 0.95)",
                backdropFilter: "blur(20px)",
                borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
                padding: "1rem",
                transform: mobileMenuOpen
                  ? "translateX(0)"
                  : "translateX(100%)",
                transition: "transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0.5rem 0.5rem 1rem",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  marginBottom: "0.5rem",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                    backgroundImage: GRADIENT_BRAND,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Menu
                </span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    padding: "0.25rem",
                  }}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {sectionIds.map((section) => (
                  <button
                    key={section}
                    onClick={() => scrollToSection(section)}
                    style={{
                      background: "none",
                      border: "none",
                      color: currentSection === section ? COLORS.cyan : "white",
                      fontSize: "1rem",
                      cursor: "pointer",
                      padding: "1rem 0.5rem",
                      textAlign: "left",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    {sectionLabels[section] || section}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content Container */}
      <div style={{ position: "relative", zIndex: 10 }}>
        {/* Hero Section */}
        <section
          id="home"
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 2rem 0",
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              textAlign: "center",
              opacity: heroLoaded ? 1 : 0,
              transform: heroLoaded ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 0.9s ease, transform 0.9s ease",
            }}
          >
            <div
              className="syl-badge-pulse"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 1.1rem",
                borderRadius: "999px",
                border: "1px solid rgba(6, 182, 212, 0.4)",
                background: "rgba(6, 182, 212, 0.08)",
                color: COLORS.cyan,
                fontSize: "0.9rem",
                fontWeight: 600,
                marginBottom: "2rem",
              }}
            >
              <Sparkles size={16} />
              Building the future, one line of code at a time
            </div>

            <div style={{ marginBottom: "3rem" }}>
              <h1
                className="syl-brand-gradient-text"
                style={{
                  fontSize: "clamp(3rem, 8vw, 6rem)",
                  fontWeight: "bold",
                  backgroundImage: GRADIENT_BRAND,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "1rem",
                  lineHeight: "1.1",
                }}
              >
                SylWave
              </h1>
              <h2
                style={{
                  fontSize: "clamp(1.5rem, 4vw, 2.5rem)",
                  color: COLORS.textSoft,
                  fontWeight: "300",
                  margin: 0,
                }}
              >
                Creating Cool Technologies
              </h2>
            </div>

            <p
              style={{
                fontSize: "1.25rem",
                color: COLORS.textMuted,
                maxWidth: "800px",
                margin: "0 auto 2.5rem auto",
                lineHeight: "1.6",
              }}
            >
              We build cutting-edge solutions that push the boundaries of
              technology. From system design platforms to revolutionary hosting
              infrastructure.
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "3rem",
              }}
            >
              <button
                onClick={() => scrollToSection("products")}
                className="syl-btn-primary"
                style={{
                  background: `linear-gradient(90deg, ${COLORS.cyan}, ${COLORS.purple})`,
                  border: "none",
                  color: "white",
                  padding: "1rem 2rem",
                  fontSize: "1.125rem",
                  fontWeight: "600",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                Explore Products <ArrowRight size={20} />
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className="syl-btn-outline"
                style={{
                  background: "transparent",
                  border: `2px solid ${COLORS.cyan}`,
                  color: COLORS.cyan,
                  padding: "1rem 2rem",
                  fontSize: "1.125rem",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(6, 182, 212, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Mail size={20} /> Get in Touch
              </button>
            </div>

            {/* Capability chips - quick, scannable signal of what we offer */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                justifyContent: "center",
              }}
            >
              {capabilityChips.map((chip) => (
                <div
                  key={chip.label}
                  className="syl-chip"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.6rem 1rem",
                    borderRadius: "999px",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    background: "rgba(255, 255, 255, 0.04)",
                    color: COLORS.textSoft,
                    fontSize: "0.9rem",
                  }}
                >
                  <span style={{ color: COLORS.purple, display: "flex" }}>
                    {chip.icon}
                  </span>
                  {chip.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section
          id="products"
          style={{
            padding: "5rem 2rem",
            background:
              "linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5))",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "4rem" }}>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.purple}, ${COLORS.cyan})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Our Products
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Revolutionary tools and platforms designed for the future
              </p>
            </Reveal>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: "2rem",
              }}
            >
              {products.map((product, index) => (
                <Reveal key={product.name} delay={index * 120}>
                  <ProductCard product={product} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section
          id="why-us"
          style={{
            padding: "5rem 2rem",
            background:
              "linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5))",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "4rem" }}>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.cyan}, ${COLORS.pink})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Why Choose SylWave
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "650px",
                  margin: "0 auto",
                }}
              >
                We don't just ship code - we build systems you can trust to stay
                up, stay fast, and stay secure
              </p>
            </Reveal>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "1.75rem",
              }}
            >
              {whyUsItems.map((item, index) => (
                <Reveal key={item.title} delay={index * 100}>
                  <div
                    className="syl-value-card"
                    style={{
                      height: "100%",
                      background: "rgba(255, 255, 255, 0.04)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "1rem",
                      padding: "2rem",
                    }}
                  >
                    <div
                      className="syl-value-icon-wrap"
                      style={{
                        display: "inline-flex",
                        padding: "0.9rem",
                        borderRadius: "0.85rem",
                        background: "rgba(255, 255, 255, 0.06)",
                        marginBottom: "1.25rem",
                      }}
                    >
                      {item.icon}
                    </div>
                    <h3
                      style={{
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        color: "white",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {item.title}
                    </h3>
                    <p
                      style={{
                        color: COLORS.textSoft,
                        lineHeight: "1.6",
                        fontSize: "0.95rem",
                        margin: 0,
                      }}
                    >
                      {item.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack Section */}
        <section id="tech" style={{ padding: "5rem 2rem" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "3.5rem" }}>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.purple}, ${COLORS.pink})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Powered By Modern Tech
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "650px",
                  margin: "0 auto",
                }}
              >
                A current, battle-tested toolkit - no legacy baggage, no
                shortcuts
              </p>
            </Reveal>

            <Reveal
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "1rem",
                justifyContent: "center",
              }}
            >
              {techStack.map((tech) => (
                <div
                  key={tech.label}
                  className="syl-tech-badge"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.85rem 1.25rem",
                    borderRadius: "0.75rem",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    background: "rgba(255, 255, 255, 0.04)",
                    color: COLORS.textSoft,
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  }}
                >
                  <span style={{ color: COLORS.cyan, display: "flex" }}>
                    {tech.icon}
                  </span>
                  {tech.label}
                </div>
              ))}
            </Reveal>
          </div>
        </section>

        {/* Process Section */}
        <section
          id="process"
          style={{
            padding: "5rem 2rem",
            background:
              "linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.5))",
          }}
        >
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "4rem" }}>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.cyan}, ${COLORS.purple})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                How We Work
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "650px",
                  margin: "0 auto",
                }}
              >
                A straightforward process from first conversation to a system
                that runs itself
              </p>
            </Reveal>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "2rem",
              }}
            >
              {processSteps.map((step, index) => (
                <Reveal key={step.title} delay={index * 100}>
                  <div
                    className="syl-process-step"
                    style={{ textAlign: "center" }}
                  >
                    <div
                      className="syl-process-number"
                      style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: `1px solid rgba(255, 255, 255, 0.15)`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 1.25rem auto",
                      }}
                    >
                      {step.icon}
                    </div>
                    <div
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        color: COLORS.cyan,
                        letterSpacing: "0.08em",
                        marginBottom: "0.5rem",
                      }}
                    >
                      STEP {index + 1}
                    </div>
                    <h3
                      style={{
                        fontSize: "1.2rem",
                        fontWeight: "bold",
                        color: "white",
                        marginBottom: "0.75rem",
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      style={{
                        color: COLORS.textSoft,
                        lineHeight: "1.6",
                        fontSize: "0.9rem",
                        margin: 0,
                      }}
                    >
                      {step.description}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Hosting Section */}
        <section id="hosting" style={{ padding: "5rem 2rem" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "4rem" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  background: "rgba(245, 158, 11, 0.12)",
                  border: "1px solid rgba(245, 158, 11, 0.35)",
                  color: COLORS.amber,
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  padding: "0.4rem 1rem",
                  borderRadius: "999px",
                  marginBottom: "1.25rem",
                }}
              >
                <Clock size={14} />
                COMING SOON
              </div>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.cyan})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Enterprise Hosting
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Professional infrastructure at an unbeatable price - currently
                in final testing ahead of launch
              </p>
            </Reveal>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "3rem",
                alignItems: "center",
              }}
            >
              <Reveal style={{ padding: "2rem 0" }}>
                <div style={{ marginBottom: "2rem" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      marginBottom: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <Server size={32} style={{ color: COLORS.cyan }} />
                    <h3
                      style={{
                        fontSize: "2.5rem",
                        color: "white",
                        fontWeight: "bold",
                        margin: 0,
                      }}
                    >
                      ₹1,99,000 Only
                    </h3>
                  </div>
                  <p
                    style={{
                      fontSize: "1.125rem",
                      color: COLORS.textSoft,
                      margin: 0,
                    }}
                  >
                    Introductory price for our complete enterprise package -
                    locked in for early sign-ups
                  </p>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "1rem",
                    marginBottom: "2rem",
                  }}
                >
                  {capabilityChips.map((feature, index) => (
                    <div
                      key={feature.label}
                      className="syl-checklist-item"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        padding: "1rem",
                        background: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "0.5rem",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        animationDelay: `${index * 100}ms`,
                      }}
                    >
                      <div style={{ color: COLORS.purple }}>{feature.icon}</div>
                      <span style={{ color: COLORS.textSoft }}>
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.6rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: COLORS.textSoft,
                      padding: "1rem 1.5rem",
                      fontSize: "1rem",
                      fontWeight: 600,
                      borderRadius: "0.5rem",
                    }}
                  >
                    <Clock size={18} style={{ color: COLORS.amber }} />
                    Launching Soon
                  </div>
                  <a
                    href="mailto:support@sylwave.com?subject=Early%20access%20-%20Enterprise%20Hosting"
                    style={{
                      color: COLORS.cyan,
                      fontSize: "0.95rem",
                      textDecoration: "underline",
                      textUnderlineOffset: "3px",
                    }}
                  >
                    Get notified when it's live →
                  </a>
                </div>
              </Reveal>

              <Reveal delay={150}>
                <div
                  style={{
                    position: "relative",
                    background: `linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(6, 182, 212, 0.2))`,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "1rem",
                    padding: "2rem",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-14px",
                      right: "1.5rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.35rem",
                      background: `linear-gradient(90deg, ${COLORS.amber}, #f97316)`,
                      color: "#1a1200",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      padding: "0.35rem 0.9rem",
                      borderRadius: "999px",
                      letterSpacing: "0.03em",
                    }}
                  >
                    <Clock size={12} />
                    COMING SOON
                  </div>
                  <h4
                    style={{
                      fontSize: "1.5rem",
                      color: "white",
                      fontWeight: "bold",
                      marginBottom: "1rem",
                    }}
                  >
                    What's Included:
                  </h4>
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {[
                      "Kubernetes orchestration",
                      "Database management",
                      "Load balancing & HA",
                      "Redis caching",
                      "Message queues",
                      "24/7 monitoring",
                    ].map((item, index) => (
                      <li
                        key={item}
                        className="syl-checklist-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                          marginBottom: "0.75rem",
                          color: COLORS.textSoft,
                          animationDelay: `${index * 90}ms`,
                        }}
                      >
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: [
                              COLORS.cyan,
                              COLORS.purple,
                              COLORS.pink,
                            ][index % 3],
                          }}
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" style={{ padding: "5rem 2rem" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <Reveal style={{ textAlign: "center", marginBottom: "3.5rem" }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  color: COLORS.cyan,
                  marginBottom: "1rem",
                }}
              >
                <HelpCircle size={22} />
                <span
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Frequently Asked Questions
                </span>
              </div>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.pink}, ${COLORS.purple})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Got Questions?
              </h2>
              <p
                style={{
                  fontSize: "1.15rem",
                  color: COLORS.textMuted,
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Everything you need to know before reaching out
              </p>
            </Reveal>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {faqItems.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <Reveal key={faq.question} delay={index * 60}>
                    <div
                      className="syl-faq-item"
                      style={{
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        background: "rgba(255, 255, 255, 0.03)",
                        borderRadius: "0.85rem",
                        overflow: "hidden",
                      }}
                    >
                      <button
                        onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                        aria-expanded={isOpen}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "1rem",
                          padding: "1.25rem 1.5rem",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <span
                          className="syl-faq-question"
                          style={{
                            fontSize: "1.05rem",
                            fontWeight: 600,
                            color: isOpen ? COLORS.cyan : "white",
                          }}
                        >
                          {faq.question}
                        </span>
                        <ChevronDown
                          size={20}
                          className={`syl-faq-chevron${
                            isOpen ? " syl-faq-open" : ""
                          }`}
                          style={{ color: COLORS.textMuted }}
                        />
                      </button>
                      <div
                        className={`syl-faq-answer${
                          isOpen ? " syl-faq-open" : ""
                        }`}
                      >
                        <div className="syl-faq-answer-inner">
                          <p
                            style={{
                              margin: 0,
                              padding: "0 1.5rem 1.25rem 1.5rem",
                              color: COLORS.textSoft,
                              lineHeight: "1.65",
                              fontSize: "0.95rem",
                            }}
                          >
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section
          id="contact"
          style={{
            padding: "5rem 2rem",
            background: "linear-gradient(to top, #000000, transparent)",
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <Reveal style={{ marginBottom: "3rem" }}>
              <h2
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 4rem)",
                  fontWeight: "bold",
                  marginBottom: "1.5rem",
                  backgroundImage: `linear-gradient(90deg, ${COLORS.cyan}, ${COLORS.purple})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Get In Touch
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: COLORS.textMuted,
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                Ready to build something amazing? Let's create the future
                together.
              </p>
            </Reveal>

            <Reveal
              delay={100}
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "2rem",
                maxWidth: "800px",
                margin: "0 auto 3rem auto",
              }}
            >
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(168, 85, 247, 0.1))",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "1rem",
                  padding: "2rem",
                  textAlign: "center",
                }}
              >
                <Mail
                  size={48}
                  style={{ color: COLORS.cyan, margin: "0 auto 1rem auto" }}
                />
                <h3
                  style={{
                    fontSize: "1.5rem",
                    color: "white",
                    fontWeight: "bold",
                    marginBottom: "1rem",
                  }}
                >
                  Email & Support
                </h3>
                <p
                  style={{
                    color: COLORS.textSoft,
                    marginBottom: "1.5rem",
                  }}
                >
                  Get in touch for any inquiries or technical support
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                  }}
                >
                  <a
                    href="mailto:support@sylwave.com"
                    className="syl-btn-outline"
                    style={{
                      display: "inline-block",
                      background: "transparent",
                      border: `2px solid ${COLORS.cyan}`,
                      color: COLORS.cyan,
                      padding: "0.75rem 1.5rem",
                      borderRadius: "0.5rem",
                      textDecoration: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        "rgba(6, 182, 212, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    support@sylwave.com
                  </a>
                  <button
                    onClick={copyEmail}
                    className="syl-copy-btn"
                    aria-label="Copy email address"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "44px",
                      height: "44px",
                      borderRadius: "0.5rem",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      background: emailCopied
                        ? "rgba(16, 185, 129, 0.15)"
                        : "rgba(255, 255, 255, 0.05)",
                      color: emailCopied ? "#10b981" : COLORS.textSoft,
                      cursor: "pointer",
                    }}
                  >
                    {emailCopied ? <Check size={18} /> : <Copy size={18} />}
                  </button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Footer */}
        <footer
          style={{
            padding: "3.5rem 0 2rem",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "2.5rem",
                justifyContent: "space-between",
                marginBottom: "2.5rem",
              }}
            >
              <div style={{ maxWidth: "340px" }}>
                <h3
                  className="syl-brand-gradient-text"
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    backgroundImage: GRADIENT_BRAND,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: "0.75rem",
                  }}
                >
                  SylWave
                </h3>
                <p
                  style={{
                    color: COLORS.textMuted,
                    fontSize: "0.95rem",
                    lineHeight: "1.6",
                    margin: 0,
                  }}
                >
                  Creating cool technologies - from system design platforms to
                  enterprise-grade cloud infrastructure, built to last.
                </p>
              </div>

              <div>
                <h4
                  style={{
                    color: "white",
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    marginBottom: "1rem",
                  }}
                >
                  Explore
                </h4>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.6rem",
                  }}
                >
                  {["products", "why-us", "process", "hosting", "faq"].map(
                    (section) => (
                      <button
                        key={section}
                        onClick={() => scrollToSection(section)}
                        style={{
                          background: "none",
                          border: "none",
                          color: COLORS.textSoft,
                          fontSize: "0.9rem",
                          cursor: "pointer",
                          padding: 0,
                          textAlign: "left",
                        }}
                      >
                        {sectionLabels[section]}
                      </button>
                    )
                  )}
                </div>
              </div>

              <div>
                <h4
                  style={{
                    color: "white",
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    marginBottom: "1rem",
                  }}
                >
                  Get In Touch
                </h4>
                <a
                  href="mailto:support@sylwave.com"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    color: COLORS.cyan,
                    fontSize: "0.9rem",
                    textDecoration: "none",
                  }}
                >
                  <Mail size={16} /> support@sylwave.com
                </a>
              </div>
            </div>

            <div
              style={{
                borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                paddingTop: "1.5rem",
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  color: COLORS.textMuted,
                  margin: 0,
                  fontSize: "0.9rem",
                }}
              >
                © 2026 SylWave. Creating the future, one line of code at a
                time.
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Back to top FAB */}
      <button
        onClick={scrollToTop}
        className="syl-fab"
        aria-label="Back to top"
        style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          zIndex: 150,
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          border: "none",
          background: `linear-gradient(135deg, ${COLORS.cyan}, ${COLORS.purple})`,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          opacity: showBackToTop ? 1 : 0,
          transform: showBackToTop ? "translateY(0)" : "translateY(16px)",
          pointerEvents: showBackToTop ? "auto" : "none",
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.4)",
        }}
      >
        <ArrowUp size={20} />
      </button>
    </div>
  );
};

export default Sylwave;
